import apiClient from "./apiClient";
import { ENDPOINTS } from "./endpoints";

export type SessionRole = "talking" | "listener";

export type SessionUser = {
  id: string;
  name?: string;
  avatar?: string;
  role?: SessionRole;
  listenerType?: string;
  points?: number;
  sessionsCompleted?: number;
};

export type Session = {
  id: string;
  _id?: string; // MongoDB ID if returned
  userId?: string | SessionUser;
  userName?: string;
  listenerId?: string | SessionUser | null;
  listenerName?: string;
  emotion: string;
  listenerType?: string;
  sessionType?: "Video" | "Audio";
  scheduledAt?: string | null;
  completedAt?: string | null;
  notes?: string;
  rtcRoomId?: string;
  status: "pending" | "active" | "completed" | "cancelled";
  callStartedAt?: string | null;
  duration?: number;
  rating?: number;
  feedback?: string;
  moodScore?: number;
  points?: number;
  earnings?: number;
  otherUser?: SessionUser; // Populate from history API
};

export type CreateSessionInput = {
  emotion: string;
  listenerType?: string;
  sessionType?: "Video" | "Audio";
  scheduledAt?: string;
  scheduledDate?: string; // YYYY-MM-DD
  scheduledTime?: string; // HH:mm
  notes?: string;
  listenerId?: string;
};

export async function createSession(body: CreateSessionInput) {
  const res = await apiClient.post(ENDPOINTS.SESSIONS.CREATE, body);
  return res.data as { success: boolean; session: Session };
}

export async function assignListener(sessionId: string) {
  const res = await apiClient.post(ENDPOINTS.SESSIONS.ASSIGN_LISTENER(sessionId), {});
  return res.data as { success: boolean; session: Session };
}

export async function declineSession(sessionId: string) {
  const res = await apiClient.post(ENDPOINTS.SESSIONS.DECLINE(sessionId), {});
  return res.data as { success: boolean; message: string };
}

export async function updateSessionStatus(
  sessionId: string, 
  body: { 
    status: Session["status"]; 
    duration?: number; 
    rating?: number; 
    feedback?: string;
    moodScore?: number;
  }
) {
  const res = await apiClient.patch(ENDPOINTS.SESSIONS.UPDATE_STATUS(sessionId), body);
  return res.data as { success: boolean; session: Session };
}

export async function getSession(sessionId: string) {
  const res = await apiClient.get(ENDPOINTS.SESSIONS.GET(sessionId));
  return res.data as { success: boolean; session: Session };
}

export type HistoryQuery = {
  page?: number;
  limit?: number;
};

export async function getHistory(query?: HistoryQuery) {
  const params = new URLSearchParams();
  if (query?.page != null) params.set("page", String(query.page));
  if (query?.limit != null) params.set("limit", String(query.limit));

  const suffix = params.toString() ? `?${params.toString()}` : "";
  const res = await apiClient.get(`${ENDPOINTS.SESSIONS.HISTORY}${suffix}`);
  return res.data as { 
    success: boolean; 
    sessions: Session[];
    pagination?: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}


export type UpcomingSessionsQuery = {
  limit?: number;
  includeUnscheduled?: boolean;
};

export async function getUpcomingSessions(_query?: UpcomingSessionsQuery) {
  // API endpoint /sessions/upcoming is not in use - commented out
  /*
  const params = new URLSearchParams();
  if (query?.limit != null) params.set("limit", String(query.limit));
  if (query?.includeUnscheduled != null) params.set("includeUnscheduled", String(query.includeUnscheduled));

  const suffix = params.toString() ? `?${params.toString()}` : "";
  const res = await apiClient.get(`${(ENDPOINTS.SESSIONS as any).UPCOMING}${suffix}`);
  return res.data as { success: boolean; sessions: Session[] };
  */
  return { success: true, sessions: [] as Session[] };
}

